import type { Holiday } from '~/lib/api/types'
import styles from './holidays-page.module.scss'

const FMT_DATE = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
})

type Props = {
  holidays: Holiday[]
  isAdmin: boolean
  onEdit: (h: Holiday) => void
  onDelete: (h: Holiday) => void
}

type YearGroup = { year: string; holidays: Holiday[] }

export function HolidayList({ holidays, isAdmin, onEdit, onDelete }: Props) {
  if (holidays.length === 0) {
    return <p className={styles.empty}>No holidays in the current window.</p>
  }

  const groups = groupByYear(holidays)

  return (
    <div>
      {groups.map(g => (
        <section key={g.year} className={styles.yearGroup} aria-label={g.year}>
          <h2 className={`${styles.yearLabel} mono`}>{g.year}</h2>
          <ul className={styles.list}>
            {g.holidays.map(h => (
              <li key={h.id} className={styles.row}>
                <span className={`${styles.rowDate} mono`}>
                  {formatRange(h)}
                </span>
                <span className={styles.rowShape} aria-hidden>
                  ◇
                </span>
                <span className={styles.rowName}>
                  {h.name}
                  {h.note && (
                    <span className={styles.rowNote}> · {h.note}</span>
                  )}
                </span>
                <span className={styles.rowActions}>
                  <SourceBadge source={h.source} />
                  <RowActions
                    holiday={h}
                    isAdmin={isAdmin}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

type RowActionsProps = {
  holiday: Holiday
  isAdmin: boolean
  onEdit: (h: Holiday) => void
  onDelete: (h: Holiday) => void
}

function RowActions({ holiday, isAdmin, onEdit, onDelete }: RowActionsProps) {
  if (holiday.source === 'gcal') {
    return (
      <a
        className={styles.gcalLink}
        href="https://calendar.google.com/calendar/u/0/r"
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Manage ${holiday.name} in Google Calendar`}
      >
        Manage in GCal →
      </a>
    )
  }
  if (!isAdmin) return null
  return (
    <>
      <button
        type="button"
        className={styles.iconBtn}
        onClick={() => onEdit(holiday)}
        aria-label={`Edit ${holiday.name}`}
      >
        Edit
      </button>
      <button
        type="button"
        className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
        onClick={() => onDelete(holiday)}
        aria-label={`Delete ${holiday.name}`}
      >
        Delete
      </button>
    </>
  )
}

function SourceBadge({ source }: { source: Holiday['source'] }) {
  const cls =
    source === 'gcal'
      ? `${styles.sourceBadge} ${styles.sourceBadgeGcal}`
      : styles.sourceBadge
  return (
    <span className={`${cls} mono`} aria-label={`source: ${source}`}>
      {source}
    </span>
  )
}

function formatRange(h: Holiday): string {
  const start = parseLocal(h.start_date)
  if (h.start_date === h.end_date) {
    return FMT_DATE.format(start)
  }
  const end = parseLocal(h.end_date)
  return `${FMT_DATE.format(start)} – ${FMT_DATE.format(end)}`
}

function parseLocal(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y as number, (m as number) - 1, d as number)
}

function groupByYear(holidays: Holiday[]): YearGroup[] {
  const map = holidays.reduce<Map<string, Holiday[]>>((acc, h) => {
    const year = h.start_date.slice(0, 4)
    const arr = acc.get(year)
    if (arr) arr.push(h)
    else acc.set(year, [h])
    return acc
  }, new Map())
  const years = Array.from(map.keys()).sort()
  return years.map(y => ({ year: y, holidays: map.get(y) as Holiday[] }))
}
